import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { CurveData } from '~/domain/patient/dvh';
import useDose from '~/hooks/patient/dose/useDose';
import useDvh from '~/hooks/patient/dose/useDvh';
import { useRoiSdfVersions } from '~/hooks/structure-set/useRoiSdfVersions';
import { roisForDvhState } from '~/state/atoms/structureSet/roiAtoms';

export interface DvhCurves {
    /** The curves computed so far, one per structure, in completion order. */
    readonly curves: CurveData[];
    /** True while curves are still being computed. */
    readonly computing: boolean;
}

/**
 * The DVH curves of the selected dose for the structures the DVH lists,
 * computed one structure at a time with a frame yielded between two, so
 * the curves appear as they complete and the UI keeps painting. A change
 * of dose, of structure list, or of a listed structure's contours starts
 * over from no curves, and a change mid-computation drops the curves of
 * the previous run. No dose, no structure, or `enabled` false gives no
 * curves and nothing computing.
 */
export function useDvhCurves(enabled = true): DvhCurves {
    const { dose } = useDose();
    const { getDvhForRoi, environment } = useDvh();
    const roisForDvh = useRecoilValue(roisForDvhState);
    const contourVersions = useRoiSdfVersions(
        environment ?? null,
        roisForDvh.map((roi) => roi.roiNumber)
    );
    const [curves, setCurves] = useState<CurveData[]>([]);
    const [computing, setComputing] = useState(false);
    const curvesRef = useRef<CurveData[]>([]);

    useEffect(() => {
        let cancelled = false;
        curvesRef.current = [];
        setCurves([]);
        if (!enabled || !dose || !roisForDvh.length) {
            return undefined;
        }
        setComputing(true);

        const computeOne = async (
            roiNumber: number
        ): Promise<CurveData | undefined> => {
            await new Promise((resolve) => requestAnimationFrame(resolve));
            if (cancelled) {
                return undefined;
            }
            return getDvhForRoi(roiNumber);
        };

        (async () => {
            const tasks = roisForDvh.map(async (roi) => {
                const curve = await computeOne(roi.roiNumber);
                if (cancelled || !curve) {
                    return;
                }
                curvesRef.current.push(curve);
                setCurves([...curvesRef.current]);
            });
            await Promise.all(tasks);
            if (!cancelled) {
                setComputing(false);
            }
        })();

        return () => {
            cancelled = true;
            setComputing(false);
        };
    }, [enabled, dose, roisForDvh, getDvhForRoi, contourVersions]);

    return { curves, computing };
}
